// The SSO entry points of the application, read once at boot by the Vue session (sessionService.js) before the
// legacy iframe exists. It replaced ssoPostHandler.js (which wrote cookies and reloaded on /auth for the legacy
// login page to read) and the legacy readers (Startup.js query string, login.js, home.js __initHome / clearCook).
//
//   ?u=<token>&a=<app>[&d&r&m&p&rp&i&c&la&action&origin]   an application handoff (BO links, delegation frame,
//                                                          customer identity provider coming back)
//   cookies __u, __a, __i, __la, __action, __origin        the same, posted to /m.eagle/auth.aspx (BO "Login As")
//   ?<CODE>                                                the customer SSO code: the login page offers its button
//   ?reset                                                 forgets that code

import { cookieUtils } from '@/utils/auth/cookieUtils';

export const HANDOFF_PARAMS = ['u', 'a', 'd', 'r', 'm', 'p', 'rp', 'i', 'c', 'la', 'action', 'origin'];
// Every cookie the handoff used, plus the customer SSO code cookies of the legacy login page.
const HANDOFF_COOKIES = [...HANDOFF_PARAMS.map((name) => `__${name}`), '__sso', '__sso_auto'];

export const SSO_CUSTOMER_KEY = 'ecosSsoCustomerCode';
// The code only says which SSO button to show: it outlives any session.
const SSO_CUSTOMER_MAX_AGE_MS = 3650 * 24 * 60 * 60 * 1000;
// login.js: these origins open a fresh session for the token they carry.
const FRESH_SESSION_ORIGINS = ['MO', 'ASSBO', 'ASSFO'];

const clean = (value) => String(value ?? '').trim();

/**
 * The handoff carried by the URL or the auth.aspx cookies, or null.
 * @param {{ query: URLSearchParams, readCookie: (name: string) => string, localhost?: boolean }} source
 */
export function readHandoff({ query, readCookie, localhost = false }) {
	const fromQuery = HANDOFF_PARAMS.some((name) => clean(query.get(name)));
	const read = (name) => clean(fromQuery ? query.get(name) : readCookie(`__${name}`));
	const values = Object.fromEntries(HANDOFF_PARAMS.map((name) => [name, read(name)]));
	if (!Object.values(values).some(Boolean)) return null;

	const token = values.u || null;
	const action = values.action || null;
	const origin = values.origin || null;
	// A URL handoff was always flagged LoginAs by the former Vue handler (ssoPostHandler → __la=1) except on
	// localhost: the session is not remembered on the device.
	const loginAs = values.la === '1' || action === 'LoginAs' || (fromQuery && Boolean(token) && !localhost);

	return {
		token,
		app: values.a || null,
		delegation: values.d || null,
		redirect: values.r === '1',
		module: values.m || null,
		param: values.p || null,
		resetPassword: values.rp === '1',
		userId: parseInt(values.i, 10) > 0 ? parseInt(values.i, 10) : null,
		customer: values.c || null,
		loginAs,
		forgetRemembered: loginAs || (Boolean(token) && FRESH_SESSION_ORIGINS.includes(origin)),
		origin,
		action,
	};
}

// Startup.js: a query string none of the handoff parameters matched is the customer SSO code. A bare word
// only: the application's own query parameters (/login?redirect=...) are not one.
export function readCustomerCode(search, query) {
	if (HANDOFF_PARAMS.some((name) => clean(query.get(name)))) return null;
	const code = clean(String(search ?? '').replace(/^\?/, ''));
	if (!code || /[=&]/.test(code)) return null;
	return decodeURIComponent(code);
}

function rememberedCustomer(storage, now) {
	try {
		const { code, ts } = JSON.parse(storage.getItem(SSO_CUSTOMER_KEY) ?? 'null') ?? {};
		if (code && now - ts <= SSO_CUSTOMER_MAX_AGE_MS) return code;
	} catch {
		// unreadable: forgotten below
	}
	storage.removeItem(SSO_CUSTOMER_KEY);
	return null;
}

export function forgetSsoCustomer(storage = globalThis.localStorage) {
	try {
		storage?.removeItem(SSO_CUSTOMER_KEY);
	} catch {
		// storage unavailable
	}
}

/**
 * Reads the boot SSO signals, then clears them: the cookies (so a reload is not another handoff) and the URL
 * query (bookmarks). Returns { handoff, ssoCustomer, ssoAuto }; ssoAuto: the code just arrived in the URL, the
 * login page goes straight to the customer identity provider (legacy __sso_auto).
 */
export function consumeSsoHandoff(win = window) {
	const query = new URLSearchParams(win.location.search);
	const localhost = ['localhost', '127.0.0.1'].includes(win.location.hostname);
	const handoff = readHandoff({ query, readCookie: (name) => cookieUtils.getDecodedCookie(name), localhost });
	const code = handoff ? null : readCustomerCode(win.location.search, query);
	const storage = win.localStorage;
	const now = Date.now();

	let ssoCustomer = null;
	let ssoAuto = false;
	if (code === 'reset') forgetSsoCustomer(storage);
	else if (code) {
		storage.setItem(SSO_CUSTOMER_KEY, JSON.stringify({ code, ts: now }));
		ssoCustomer = code;
		ssoAuto = true;
	} else {
		ssoCustomer = rememberedCustomer(storage, now) ?? (cookieUtils.getDecodedCookie('__sso') || null);
		if (ssoCustomer) storage.setItem(SSO_CUSTOMER_KEY, JSON.stringify({ code: ssoCustomer, ts: now }));
	}

	HANDOFF_COOKIES.forEach((name) => cookieUtils.deleteCookie(name));
	if (handoff || code) win.history.replaceState(win.history.state, '', `${win.location.pathname}${win.location.hash}`);

	return { handoff, ssoCustomer, ssoAuto };
}
